// ./api/auditLogAPI.js

import axiosInstance from '../utilities/axiosInstance';

const auditLogAPI = {
  // ============================================================================
  // CREATE / ADD LOGS
  // ============================================================================

  // Add a new audit log entry
  addLog: async (logData) => {
    try {
      const response = await axiosInstance.post('/api/log/add', logData);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to add log: ${error.message}` };
    }
  },

  // ============================================================================
  // READ / GET LOGS
  // ============================================================================

  // Get all audit logs for a specific student
  getStudentLogs: async (studentId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/api/log/student/${studentId}`, { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get student logs: ${error.message}` };
    }
  },

  // Get audit logs for any entity
  getEntityLogs: async (entityType, entityId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/api/log/entity/${entityType}/${entityId}`, { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get entity logs: ${error.message}` };
    }
  },

  // Get activity logs for a specific user
  getUserActivity: async (userId, params = {}) => {
    try {
      const response = await axiosInstance.get(`/api/log/user/${userId}/activity`, { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get user activity: ${error.message}` };
    }
  },

  // Get logs by action type
  getLogsByAction: async (action, params = {}) => {
    try {
      const response = await axiosInstance.get(`/api/log/action/${action}`, { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get logs by action: ${error.message}` };
    }
  },

  // ============================================================================
  // SECURITY & MONITORING
  // ============================================================================

  // Get security-related events
  getSecurityEvents: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/log/security/events', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get security events: ${error.message}` };
    }
  },

  // Get failed login attempts
  getFailedLoginAttempts: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/log/security/failed-logins', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get failed login attempts: ${error.message}` };
    }
  },

  // Get critical severity events
  getCriticalEvents: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/log/critical', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get critical events: ${error.message}` };
    }
  },

  // ============================================================================
  // REPORTING & ANALYTICS
  // ============================================================================

  // Generate compliance report
  getComplianceReport: async (params) => {
    try {
      const response = await axiosInstance.get('/api/log/compliance/report', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get compliance report: ${error.message}` };
    }
  },

  // Get system-wide activity
  getSystemActivity: async (params) => {
    try {
      const response = await axiosInstance.get('/api/log/system/activity', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get system activity: ${error.message}` };
    }
  },

  // Get audit log statistics
  getStatistics: async (params = {}) => {
    try {
      const response = await axiosInstance.get('/api/log/statistics', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get statistics: ${error.message}` };
    }
  },

  // Search audit logs
  searchLogs: async (params) => {
    try {
      const response = await axiosInstance.get('/api/log/search', { params });
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to search logs: ${error.message}` };
    }
  },

  // ============================================================================
  // MAINTENANCE
  // ============================================================================

  // Cleanup old audit logs
  cleanupOldLogs: async (cleanupData) => {
    try {
      const response = await axiosInstance.post('/api/log/cleanup', cleanupData);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to cleanup logs: ${error.message}` };
    }
  },

  // Export all activity logs for a user
  exportActivities: async (loginId) => {
    try {
      const response = await axiosInstance.get(`/api/log/exportactivities/${loginId}`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to export activities: ${error.message}` };
    }
  },

  // Get all audit actions for a role
  getActionsByRole: async (role) => {
    try {
      const response = await axiosInstance.get(`/api/log/actionsbyRole/${role}`);
      return response.data;
    } catch (error) {
      return error?.response?.data || { success: false, error: `Failed to get actions by role: ${error.message}` };
    }
  },
};

export default auditLogAPI;